import React, { useState, useMemo, useCallback, useEffect } from "react";
import { ThemeContext } from "./context";
import Helmet from "react-helmet";
import {
  StyleSheetManager,
  ThemeProvider as StyledComponentThemeProvider,
} from "styled-components";
import darkThemeVariables from "./dark";
import lightThemeVariables from "./light";
import commonThemeVariables from "./common";
import { appConfigService } from "services/appConfigService";
import { SkeletonTheme } from "react-loading-skeleton";

//@ts-ignore
import dark from "./less/dark.theme.less";
//@ts-ignore
import light from "./less/light.theme.less";
import useSizeMediaQuery from "hooks/useSizeMediaQuery";
import { isMicroApp } from "utils/microApp";

const themeMap = {
  dark,
  light,
};
interface IProps {
  children?: any;
}

const ThemeProvider = (props: IProps) => {
  const { children } = props;
  const [theme, setTheme] = useState<"light" | "dark">(
    appConfigService.getItem("theme")
  );

  const handleThemeChange = useCallback(
    (t: "light" | "dark") => {
      setTheme(t);
      appConfigService.setItem("theme", t);
    },
    [setTheme]
  );

  const themeVariables = useMemo(
    () => ({
      ...(theme === "dark" ? darkThemeVariables : lightThemeVariables),
      ...commonThemeVariables,
    }),
    [theme]
  );

  useEffect(() => {
    const styles = themeMap[theme];
    styles?.use();
    return () => {
      styles?.unuse();
    };
  }, [theme]);

  const size = useSizeMediaQuery();

  const isSmall = size === "sm" || size === "xs";

  return (
    <ThemeContext.Provider value={{ theme, setTheme: handleThemeChange }}>
      <Helmet>
        <meta name="theme-color" content={themeVariables.background_color} />
        <meta
          name="background-color"
          content={themeVariables.background_color}
        />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content={theme === "dark" ? "black-translucent" : "default"}
        />
        <style></style>
      </Helmet>
      <StyleSheetManager disableCSSOMInjection={isMicroApp}>
        <StyledComponentThemeProvider theme={themeVariables}>
          <SkeletonTheme
            color={themeVariables.background_color}
            highlightColor={theme === "dark" ? "#444" : undefined}
          >
            <Helmet>
              <style>
                {isSmall &&
                  `html {
                margin: 0;
                padding: 0;
                overflow: hidden;
              }
              body {
                position: absolute;
                width: 100%;
                height: 100%;
                overflow: auto;
              }`}
              </style>
              <style>
                {theme === "dark" &&
                  `:root{color-scheme:dark;};html{scrollbar-face-color:#646464;scrollbar-base-color:#646464;scrollbar-3dlight-color:#646464;scrollbar-highlight-color:#646464;scrollbar-track-color:#000;scrollbar-arrow-color:#000;scrollbar-shadow-color:#646464;scrollbar-dark-shadow-color:#646464}::-webkit-scrollbar-button{display:none}::-webkit-scrollbar-track{background-color:#646464}::-webkit-scrollbar-track-piece{background-color:#000}::-webkit-scrollbar-thumb{height:50px;background-color:#666;border-radius:3px}::-webkit-scrollbar-corner{background-color:#646464}`}
              </style>
            </Helmet>
            {children}
          </SkeletonTheme>
        </StyledComponentThemeProvider>
      </StyleSheetManager>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
